import { Box, Chip, Typography } from '@mui/material';
import React from 'react';
import { themeColors } from '../../JuvareTheme';
import { ContentBase } from './CardContentBase';
import { FeatureIcons } from './FeatureIcons';
/**
 * Renders the content section of a Card, including title, badges, and optional feature icons.
 *
 * @param {CardContentProps} props - Props for configuring card content.
 * @param {string} props.title - Title displayed in the card content.
 * @param {string} [props.badge] - Optional top-aligned badge shown above the title.
 * @param {string} [props.inlineBadge] - Optional inline badge shown next to the title.
 * @param {React.ReactNode} [props.featureIcons] - Optional feature icons displayed below the title.
 * @param {() => void} [props.onClick] - Callback when the content section is clicked.
 * @param {string} [props.ariaLabel] - ARIA label for the content container for accessibility.
 *
 * @returns {React.ReactElement} The rendered CardContent element.
 */
export const CardContent = (props) => {
    const hasIcons = props.featureIcons !== undefined;
    const hasBadge = props.badge !== undefined;
    return (React.createElement(ContentBase, { hasBadge: hasBadge, hasIcons: hasIcons, onClick: props.onClick, ariaLabel: props.ariaLabel },
        props.badge ? (React.createElement(Box, { sx: { pt: 1, gridColumn: 1, display: 'flex', justifyContent: 'flex-start' }, "data-test": 'badge' },
            React.createElement(Chip, { sx: { color: themeColors.accent.grey.bold, backgroundColor: themeColors.accent.grey.subtlest }, label: props.badge, size: 'small' }))) : null,
        React.createElement(Box, { sx: {
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                alignItems: 'center',
                gap: 1,
                pt: !hasBadge ? (!hasIcons ? 0 : 4) : 0,
            } },
            React.createElement(Typography, { component: 'p', variant: 'h6', sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }, "data-test": 'title' }, props.title),
            props.inlineBadge ? (React.createElement(Chip, { sx: { color: themeColors.text.inverse, backgroundColor: themeColors.accent.red.bold }, label: props.inlineBadge, size: 'small', "data-test": 'inline-badge' })) : null),
        props.featureIcons ? React.createElement(FeatureIcons, null, props.featureIcons) : null));
};
